/**
 * Zeichnet das Massband.
 *
 * Warum Canvas und nicht DOM oder ein CSS-Verlauf: Skala, Beschriftung und
 * angezeigter Messwert muessen zwingend aus derselben Zahl entstehen. Ein
 * CSS-Verlauf haengt dagegen an der Scroll-Position des Elements - und die
 * springt beim Zuruecksetzen der Bahn. Dann waeren Skala und Messwert um
 * Zentimeter versetzt, und ausgerechnet das Messgeraet wuerde luegen.
 *
 * Hier haengt alles an `forwardCm`. Rueckwaertsscrollen bewegt nichts, weil es
 * nicht zaehlt - die Regel ist damit ohne Erklaertext sichtbar.
 */

export interface TapePalette {
  background: string
  minorTick: string
  majorTick: string
  label: string
  referenceLine: string
  marker: string
  markerLabel: string
}

export const DEFAULT_PALETTE: TapePalette = {
  background: '#0d1117',
  minorTick: 'rgba(226, 232, 240, 0.28)',
  majorTick: 'rgba(226, 232, 240, 0.75)',
  label: 'rgba(226, 232, 240, 0.85)',
  referenceLine: '#ff5252',
  marker: '#ffd23f',
  markerLabel: '#0d1117',
}

export interface TapeMarker {
  /** Position auf der Bahn in Zentimetern. */
  cm: number
  label: string
}

export interface DrawTapeOptions {
  ctx: CanvasRenderingContext2D
  /** Breite und Hoehe in CSS-Pixeln. */
  width: number
  height: number
  /** Aktuell zurueckgelegte Strecke in Zentimetern. */
  forwardCm: number
  /** Wie viele CSS-Pixel ein Zentimeter auf diesem Geraet misst. */
  pxPerCm: number
  /** Hoehe der Messlinie als Anteil der Gesamthoehe. */
  referenceRatio: number
  markers: TapeMarker[]
  palette: TapePalette
}

/**
 * Moegliche Abstaende der Skalenstriche in Zentimetern.
 * Aus dieser Leiter waehlt die Zeichnung den feinsten Abstand, der noch lesbar
 * ist - genau wie eine Landkarte den Massstab wechselt, statt bei hoher
 * Geschwindigkeit zur schwarzen Flaeche zu werden.
 */
const STEP_LADDER_CM = [1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10_000, 100_000]

/** Ein Skalenstrich unter diesem Abstand ist nicht mehr unterscheidbar. */
const MIN_TICK_SPACING_PX = 11

export function drawTape(options: DrawTapeOptions): void {
  const { ctx, width, height, forwardCm, pxPerCm, referenceRatio, markers, palette } = options

  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = palette.background
  ctx.fillRect(0, 0, width, height)

  const referenceY = Math.round(height * referenceRatio)
  const minorStep = chooseStep(pxPerCm)
  const majorStep = minorStep * 10

  // Sichtbarer Streckenbereich: oberhalb der Messlinie liegt bereits
  // Zurueckgelegtes, unterhalb das noch Bevorstehende.
  const cmAboveLine = referenceY / pxPerCm
  const cmBelowLine = (height - referenceY) / pxPerCm
  const fromCm = forwardCm - cmAboveLine
  const toCm = forwardCm + cmBelowLine

  const yFor = (cm: number): number => referenceY + (cm - forwardCm) * pxPerCm

  ctx.lineWidth = 1
  ctx.textBaseline = 'middle'
  ctx.font = '500 13px ui-monospace, "SF Mono", "Cascadia Mono", Menlo, monospace'

  const firstTick = Math.ceil(fromCm / minorStep) * minorStep
  for (let cm = firstTick; cm <= toCm; cm += minorStep) {
    // Fliesskomma-Addition driftet; auf den Raster-Schritt zurueckrunden.
    const value = Math.round(cm / minorStep) * minorStep
    if (value < 0) continue
    const y = Math.round(yFor(value)) + 0.5
    const isMajor = Math.abs(value % majorStep) < minorStep / 2

    ctx.strokeStyle = isMajor ? palette.majorTick : palette.minorTick
    ctx.beginPath()
    ctx.moveTo(0, y)
    ctx.lineTo(isMajor ? width * 0.24 : width * 0.11, y)
    ctx.stroke()

    if (isMajor) {
      ctx.fillStyle = palette.label
      ctx.fillText(tickLabel(value), width * 0.24 + 10, y)
    }
  }

  drawMarkers(ctx, markers, yFor, width, height, palette)
  drawReferenceLine(ctx, referenceY, width, palette)
}

/** Feinster Strichabstand, der bei dieser Pixeldichte noch auseinanderzuhalten ist. */
function chooseStep(pxPerCm: number): number {
  for (const step of STEP_LADDER_CM) {
    if (step * pxPerCm >= MIN_TICK_SPACING_PX) return step
  }
  return STEP_LADDER_CM[STEP_LADDER_CM.length - 1]
}

/** Beschriftung im Massstab des Wertes: cm bis 1 m, dann m, ab 1 km km. */
function tickLabel(cm: number): string {
  if (cm < 100) return `${round(cm, 0)} cm`
  if (cm < 100_000) return `${formatDe(cm / 100, cm % 100 === 0 ? 0 : 1)} m`
  return `${formatDe(cm / 100_000, 2)} km`
}

function drawMarkers(
  ctx: CanvasRenderingContext2D,
  markers: TapeMarker[],
  yFor: (cm: number) => number,
  width: number,
  height: number,
  palette: TapePalette,
): void {
  for (const marker of markers) {
    const y = yFor(marker.cm)
    // Ausserhalb des Bildes klemmen, damit ein noch fernes Ziel als Hinweis am
    // unteren Rand stehen bleibt statt unsichtbar zu sein.
    const clampedY = Math.max(14, Math.min(height - 14, y))
    const isOffscreen = Math.abs(clampedY - y) > 1

    ctx.save()
    ctx.strokeStyle = palette.marker
    ctx.setLineDash(isOffscreen ? [4, 5] : [])
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(0, Math.round(clampedY) + 0.5)
    ctx.lineTo(width, Math.round(clampedY) + 0.5)
    ctx.stroke()
    ctx.restore()

    const text = marker.label
    ctx.font = '600 12px ui-sans-serif, system-ui, sans-serif'
    const padding = 7
    const textWidth = ctx.measureText(text).width
    const boxWidth = textWidth + padding * 2
    const boxX = width - boxWidth - 10
    const boxY = clampedY - 11

    ctx.fillStyle = palette.marker
    roundedRect(ctx, boxX, boxY, boxWidth, 22, 4)
    ctx.fill()

    ctx.fillStyle = palette.markerLabel
    ctx.textBaseline = 'middle'
    ctx.fillText(text, boxX + padding, clampedY)
  }
}

/** Die Messlinie: hier wird abgelesen, wie bei einem Tacho die Nadel. */
function drawReferenceLine(
  ctx: CanvasRenderingContext2D,
  y: number,
  width: number,
  palette: TapePalette,
): void {
  ctx.save()
  ctx.strokeStyle = palette.referenceLine
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(0, y + 0.5)
  ctx.lineTo(width, y + 0.5)
  ctx.stroke()

  ctx.fillStyle = palette.referenceLine
  ctx.beginPath()
  ctx.moveTo(0, y - 7)
  ctx.lineTo(11, y)
  ctx.lineTo(0, y + 7)
  ctx.closePath()
  ctx.fill()
  ctx.restore()
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

function round(value: number, decimals: number): number {
  const factor = 10 ** decimals
  return Math.round(value * factor) / factor
}

function formatDe(value: number, decimals: number): string {
  return value.toLocaleString('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}
